import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  AlertTriangle, Calendar, BarChart, FileText, 
  Bookmark, ExternalLink, ChevronDown, ChevronUp, ArrowRight, Activity,
  Shield, Globe, Flag, Users, Clock, Info, AlertCircle
} from 'lucide-react';
import CardContainer from './CardContainer';
import { MobileDetailView, LoadingIndicator } from './mobile';

const PolicyDetailView = ({ policyId, policySlug, isAdmin = false, disableEditButton = false }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [showFullSummary, setShowFullSummary] = useState(true);
  const tabsContainerRef = useRef(null);
  const leftIndicatorRef = useRef(null);
  const rightIndicatorRef = useRef(null);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Setup scroll indicators for tabs
  useEffect(() => {
    const tabsContainer = tabsContainerRef.current;
    const leftIndicator = leftIndicatorRef.current;
    const rightIndicator = rightIndicatorRef.current;
    
    if (!tabsContainer || !leftIndicator || !rightIndicator) return;
    
    // Function to check overflow and update indicators
    const updateScrollIndicators = () => {
      const hasOverflow = tabsContainer.scrollWidth > tabsContainer.clientWidth;
      const scrolledToStart = tabsContainer.scrollLeft <= 0;
      const scrolledToEnd = tabsContainer.scrollLeft + tabsContainer.clientWidth >= tabsContainer.scrollWidth - 10;
      
      // Update indicator visibility
      leftIndicator.style.opacity = (hasOverflow && !scrolledToStart) ? '1' : '0';
      rightIndicator.style.opacity = (hasOverflow && !scrolledToEnd) ? '1' : '0';
      
      // Add a subtle pulse animation to the right indicator when first loaded
      if (hasOverflow && scrolledToStart && !tabsContainer.dataset.hintShown) {
        tabsContainer.dataset.hintShown = 'true';
        rightIndicator.classList.add('animate-pulse');
        setTimeout(() => {
          rightIndicator.classList.remove('animate-pulse');
        }, 2000);
      }
    };
    
    // Run once on mount
    updateScrollIndicators();
    
    // Setup listeners
    tabsContainer.addEventListener('scroll', updateScrollIndicators);
    window.addEventListener('resize', updateScrollIndicators);
    
    // Add a subtle scroll animation on mobile to hint that tabs are scrollable
    if (isMobile) {
      setTimeout(() => {
        if (tabsContainer.scrollWidth > tabsContainer.clientWidth) {
          // Scroll a little bit to show there's more content, then scroll back
          tabsContainer.scrollBy({ left: 40, behavior: 'smooth' });
          setTimeout(() => {
            tabsContainer.scrollBy({ left: -40, behavior: 'smooth' });
          }, 800);
        }
      }, 1500);
    }
    
    // Cleanup
    return () => {
      tabsContainer.removeEventListener('scroll', updateScrollIndicators);
      window.removeEventListener('resize', updateScrollIndicators);
    };
  }, [isMobile]);
  
  // Fetch policy data on component mount
  useEffect(() => {
    const fetchPolicy = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let url;
        if (policyId) {
          // Use ID directly - most reliable
          url = `/api/policies/${policyId}`;
          console.log(`PolicyDetailView: Fetching policy with ID ${policyId}, isAdmin=${isAdmin}`);
        } else if (policySlug) {
          // Use slug - less reliable but works for backward compatibility
          url = `/api/policies/slug/${policySlug}`;
          console.log(`PolicyDetailView: Fetching policy with slug ${policySlug}, isAdmin=${isAdmin}`);
        } else {
          throw new Error('Policy ID or slug is required');
        }
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`Error fetching policy: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
          setPolicy(data.policy);
          console.log(`PolicyDetailView: Policy loaded successfully - ID: ${data.policy.id}, status: ${data.policy.status}`);
        } else {
          throw new Error(data.error || 'Failed to fetch policy');
        }
      } catch (err) {
        console.error('Error fetching policy:', err);
        setError(err.message || 'Failed to load policy');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPolicy();
  }, [policyId, policySlug, isAdmin]);
  
  // Get threat level color - using slate color scheme for consistency with articles
  const getThreatLevelColor = (score) => {
    if (score >= 81) return 'bg-slate-300 text-slate-800 border-slate-400';
    if (score >= 61) return 'bg-slate-300 text-slate-800 border-slate-400';
    if (score >= 41) return 'bg-slate-200 text-slate-800 border-slate-300';
    if (score >= 21) return 'bg-slate-200 text-slate-800 border-slate-300';
    return 'bg-slate-100 text-slate-800 border-slate-200';
  };
  
  // Get threat level label
  const getThreatLevelLabel = (score) => {
    if (score >= 81) return 'Severe';
    if (score >= 61) return 'Significant';
    if (score >= 41) return 'Moderate';
    if (score >= 21) return 'Mild';
    return 'Minimal';
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };
  
  // Get severity color - using slate color scheme for consistency with articles
  const getSeverityColor = (severity) => {
    switch(severity) {
      case 5: return 'bg-slate-300 text-slate-800 border-slate-400';
      case 4: return 'bg-slate-300 text-slate-800 border-slate-400';
      case 3: return 'bg-slate-200 text-slate-800 border-slate-300';
      case 2: return 'bg-slate-200 text-slate-800 border-slate-300';
      case 1: return 'bg-slate-100 text-slate-800 border-slate-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  // Get severity label
  const getSeverityLabel = (severity) => {
    switch(severity) {
      case 5: return 'Critical';
      case 4: return 'High';
      case 3: return 'Moderate';
      case 2: return 'Low';
      case 1: return 'Minimal';
      default: return 'Unknown';
    }
  };
  
  // Progress bar component
  const ProgressBar = ({ progress, colorClass = "bg-blue-600" }) => {
    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full ${colorClass}`}
          style={{ width: `${progress}%` }}
        ></div>
      </div>
    );
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto w-full">
          <LoadingIndicator 
            size="large" 
            label="Loading policy details..." 
            fullPage={false} 
          />
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <MobileDetailView
          backUrl="/p/"
          backLabel="Policies"
          contentClassName="w-full"
        >
          <CardContainer>
            <div className="p-6">
              <div className="flex items-start">
                <AlertCircle className="text-red-500 mr-4 mt-1" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-red-700 mb-2">Error loading policy</h3>
                  <p className="text-gray-700 mb-4">{error}</p>
                </div>
              </div>
            </div>
          </CardContainer>
        </MobileDetailView>
      </div>
    );
  }
  
  // Not found state
  if (!policy) {
    return (
      <div className="container mx-auto px-4 py-8">
        <MobileDetailView
          backUrl="/p/"
          backLabel="Policies"
          contentClassName="w-full"
        >
          <CardContainer>
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-4">Policy not found</p>
            </div>
          </CardContainer>
        </MobileDetailView>
      </div>
    );
  }
  
  return (
    <>
      {/* SEO and Schema.org markup */}
      <Helmet>
        <title>{policy.title ? `Policy Analysis: ${policy.title}` : 'Policy Analysis'}</title>
        <meta name="description" content={policy.summary || `Analysis of policy: ${policy.title}`} />
        
        {/* Canonical URL - formatted to ensure consistency with ID at end of slug */}
        <link rel="canonical" href={`${window.location.origin}/p/${
          policy.title 
            ? policy.title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').substring(0, 50).replace(/-+$/, '') 
            : 'policy'
        }-${policy.id}.html`} />
        
        {/* Debug information for troubleshooting */}
        <meta name="policy-id" content={policy.id || 'No ID'} />
        <meta name="policy-slug" content={policy.slug || 'No slug'} />
        
        {/* Open Graph meta tags */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={policy.title ? `Policy Analysis: ${policy.title}` : 'Policy Analysis'} />
        <meta property="og:description" content={policy.summary || `Analysis of policy: ${policy.title}`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content={`${window.location.origin}/images/social-share.jpg`} />
        <meta property="og:site_name" content="Democracy Analysis Project" />
        
        {/* Mobile optimization meta tags */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, minimum-scale=1.0, viewport-fit=cover" />
      </Helmet>
      
      <div className="w-full">
        <MobileDetailView
          backUrl="/p/"
          backLabel="Policies"
          contentClassName="w-full"
        >
          {/* Main content area */}
          <div className="w-full">
        {/* Main Card containing all sections */}
        <CardContainer>
          {/* Policy header with title, date, source link, and threat level */}
          <div className="p-4 sm:p-6 border-b border-gray-100">
            {/* Full-width title */}
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              {policy.title}
            </h1>
            
            {/* Metadata and threat level in a row */}
            <div className="flex flex-col md:flex-row justify-between items-start">
              {/* Left side - Date and source link */}
              <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-3 md:mb-0">
                <div className="flex items-center mobile-touch-target">
                  <Calendar className="w-4 h-4 mr-1.5 opacity-70" />
                  <span className="font-medium text-sm">
                    {formatDate(policy.date)}
                  </span>
                </div>
                
                {policy.source_link && (
                  <div className="flex items-center mobile-touch-target">
                    <ExternalLink className="w-4 h-4 mr-1.5 opacity-70" />
                    <a 
                      href={policy.source_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-medium text-sm text-blue-600"
                    >
                      View source
                    </a>
                  </div>
                )}
                
                {isAdmin && !disableEditButton && (
                  <Link 
                    to={`/admin/policies/${policy.id}/edit`}
                    className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors mobile-touch-target"
                  >
                    Edit policy
                  </Link>
                )}
              </div>
              
              {/* Right side - Threat level and score */}
              <div className="flex flex-col md:items-end md:self-start">
                <div className={`text-sm font-medium px-3 py-1.5 rounded-full border mobile-touch-target ${getThreatLevelColor(policy.score)}`}>
                  {getThreatLevelLabel(policy.score)} Threat Level
                </div>
                  
                <div className="text-sm text-gray-700 mt-2 text-center md:text-right">
                  <span className="font-bold text-lg">{policy.score}/100</span>
                  <span className="ml-1">Score</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Score visualization */}
          <div className="px-6 py-4 bg-gray-50">
            <div className="text-sm font-medium text-gray-700 mb-4">Threat Assessment Breakdown</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Authoritarian Tactics</span>
                  <span className="text-sm font-medium text-gray-700">{policy.score_tactics}/100</span>
                </div>
                <ProgressBar progress={policy.score_tactics} colorClass="bg-slate-500" />
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Constitutional Issues</span>
                  <span className="text-sm font-medium text-gray-700">{policy.score_constitutional}/100</span>
                </div>
                <ProgressBar progress={policy.score_constitutional} colorClass="bg-slate-500" />
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Legal Precedents</span>
                  <span className="text-sm font-medium text-gray-700">{policy.score_precedents}/100</span>
                </div>
                <ProgressBar progress={policy.score_precedents} colorClass="bg-slate-500" />
              </div>
            </div>
          </div>
          
          {/* Tab navigation */}
          <div className="relative border-b border-gray-200">
            <div 
              ref={leftIndicatorRef}
              className="absolute left-0 top-0 bottom-0 flex items-center justify-center opacity-0 transition-opacity w-8 z-10"
              style={{ 
                background: 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                pointerEvents: 'none' 
              }}
            >
              <div className="h-8 w-8 flex items-center justify-center bg-white rounded-full shadow-md">
                <ChevronLeft size={20} />
              </div>
            </div>
            
            <div 
              ref={tabsContainerRef}
              className="flex overflow-x-auto py-1 px-1 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <button
                onClick={() => setActiveTab('overview')}
                className={`whitespace-nowrap py-3 sm:py-4 px-3 sm:px-4 border-b-2 font-medium text-sm flex items-center mobile-touch-target ${
                  activeTab === 'overview'
                    ? 'border-slate-500 text-slate-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="inline-block h-4 w-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">Overview</span>
              </button>
              
              <button
                onClick={() => setActiveTab('tactics')}
                className={`whitespace-nowrap py-3 sm:py-4 px-3 sm:px-4 border-b-2 font-medium text-sm flex items-center mobile-touch-target ${
                  activeTab === 'tactics'
                    ? 'border-slate-500 text-slate-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <AlertTriangle className="inline-block h-4 w-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">Tactics</span>
              </button>
              
              <button
                onClick={() => setActiveTab('constitutional')}
                className={`whitespace-nowrap py-3 sm:py-4 px-3 sm:px-4 border-b-2 font-medium text-sm flex items-center mobile-touch-target ${
                  activeTab === 'constitutional'
                    ? 'border-slate-500 text-slate-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Shield className="inline-block h-4 w-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">Constitutional</span>
              </button>
              
              <button
                onClick={() => setActiveTab('precedents')}
                className={`whitespace-nowrap py-3 sm:py-4 px-3 sm:px-4 border-b-2 font-medium text-sm flex items-center mobile-touch-target ${
                  activeTab === 'precedents'
                    ? 'border-slate-500 text-slate-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Globe className="inline-block h-4 w-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">Precedents</span>
              </button>
              
              <button
                onClick={() => setActiveTab('insights')}
                className={`whitespace-nowrap py-3 sm:py-4 px-3 sm:px-4 border-b-2 font-medium text-sm flex items-center mobile-touch-target ${
                  activeTab === 'insights'
                    ? 'border-slate-500 text-slate-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Bookmark className="inline-block h-4 w-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">Insights</span>
              </button>
            </div>
            
            <div 
              ref={rightIndicatorRef}
              className="absolute right-0 top-0 bottom-0 flex items-center justify-center opacity-0 transition-opacity w-8 z-10"
              style={{ 
                background: 'linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                pointerEvents: 'none' 
              }}
            >
              <div className="h-8 w-8 flex items-center justify-center bg-white rounded-full shadow-md">
                <ChevronRight size={20} />
              </div>
            </div>
          </div>
          
          {/* Tab content */}
          <div className="p-4 sm:p-6">
            {/* Overview tab */}
            {activeTab === 'overview' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Summary</h2>
                <div className="prose max-w-none">
                  {policy.summary ? (
                    <div className="whitespace-pre-line">
                      <div className={`prose max-w-none ${!showFullSummary && policy.summary.length > 500 ? 'line-clamp-4' : ''}`} 
                           dangerouslySetInnerHTML={{ 
                             __html: policy.summary
                               .split('\n\n')
                               .map(para => `<p>${para}</p>`)
                               .join('')
                           }} 
                      />
                      
                      {policy.summary.length > 500 && (
                        <button
                          onClick={() => setShowFullSummary(!showFullSummary)}
                          className="mt-2 text-slate-600 text-sm font-medium hover:text-slate-800 flex items-center"
                        >
                          {showFullSummary ? (
                            <>
                              <ChevronUp className="h-4 w-4 mr-1" />
                              Show less
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-4 w-4 mr-1" />
                              Read more
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No summary available for this policy.</p>
                  )}
                </div>
                
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Findings</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Tactics summary */}
                    <div className="border rounded-lg p-4 shadow-sm">
                      <h4 className="font-medium flex items-center text-slate-700 mb-2">
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        Authoritarian Tactics
                      </h4>
                      {policy.tactics && policy.tactics.length > 0 ? (
                        <ul className="list-disc list-inside text-sm space-y-1">
                          {policy.tactics.slice(0, 3).map((tactic, index) => (
                            <li key={index} className="text-gray-700">{tactic.tactic_name}</li>
                          ))}
                          {policy.tactics.length > 3 && (
                            <li className="text-slate-600 cursor-pointer" onClick={() => setActiveTab('tactics')}>
                              +{policy.tactics.length - 3} more...
                            </li>
                          )}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No tactics identified</p>
                      )}
                    </div>
                    
                    {/* Constitutional issues summary */}
                    <div className="border rounded-lg p-4 shadow-sm">
                      <h4 className="font-medium flex items-center text-slate-700 mb-2">
                        <Shield className="h-4 w-4 mr-1" />
                        Constitutional Issues
                      </h4>
                      {policy.constitutional_issues && policy.constitutional_issues.length > 0 ? (
                        <ul className="list-disc list-inside text-sm space-y-1">
                          {policy.constitutional_issues.slice(0, 3).map((issue, index) => (
                            <li key={index} className="text-gray-700">{issue.issue_name}</li>
                          ))}
                          {policy.constitutional_issues.length > 3 && (
                            <li className="text-slate-600 cursor-pointer" onClick={() => setActiveTab('constitutional')}>
                              +{policy.constitutional_issues.length - 3} more...
                            </li>
                          )}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No constitutional issues identified</p>
                      )}
                    </div>
                    
                    {/* Precedents summary */}
                    <div className="border rounded-lg p-4 shadow-sm">
                      <h4 className="font-medium flex items-center text-slate-700 mb-2">
                        <Globe className="h-4 w-4 mr-1" />
                        Legal Precedents
                      </h4>
                      {policy.precedents && policy.precedents.length > 0 ? (
                        <ul className="list-disc list-inside text-sm space-y-1">
                          {policy.precedents.slice(0, 3).map((precedent, index) => (
                            <li key={index} className="text-gray-700">{precedent.precedent_name}</li>
                          ))}
                          {policy.precedents.length > 3 && (
                            <li className="text-slate-600 cursor-pointer" onClick={() => setActiveTab('precedents')}>
                              +{policy.precedents.length - 3} more...
                            </li>
                          )}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No legal precedents identified</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Tactics tab */}
            {activeTab === 'tactics' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Authoritarian Tactics</h2>
                
                {policy.tactics && policy.tactics.length > 0 ? (
                  <div className="space-y-6">
                    {policy.tactics.map((tactic, index) => (
                      <div key={index} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                          <h3 className="font-medium text-gray-900">{tactic.tactic_name}</h3>
                          <div className={`text-xs font-medium px-2 py-1 rounded-full border ${getSeverityColor(tactic.severity)}`}>
                            {getSeverityLabel(tactic.severity)} Severity
                          </div>
                        </div>
                        
                        <div className="p-4">
                          <div className="text-sm text-gray-700 mb-4">{tactic.description}</div>
                          
                          {tactic.evidence && (
                            <div className="mt-2">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Evidence from policy:</h4>
                              <blockquote className="text-sm italic border-l-4 border-gray-200 pl-4 py-2 text-gray-600">
                                {tactic.evidence}
                              </blockquote>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <AlertTriangle className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <h3 className="text-lg font-medium text-gray-900">No authoritarian tactics identified</h3>
                    <p className="mt-1 text-gray-500">This policy does not appear to employ notable authoritarian tactics.</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Constitutional issues tab */}
            {activeTab === 'constitutional' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Constitutional Issues</h2>
                
                {policy.constitutional_issues && policy.constitutional_issues.length > 0 ? (
                  <div className="space-y-6">
                    {policy.constitutional_issues.map((issue, index) => (
                      <div key={index} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                          <h3 className="font-medium text-gray-900">{issue.issue_name}</h3>
                          <div className={`text-xs font-medium px-2 py-1 rounded-full border ${getSeverityColor(issue.severity)}`}>
                            {getSeverityLabel(issue.severity)} Severity
                          </div>
                        </div>
                        
                        <div className="p-4">
                          <div className="text-sm text-gray-700 mb-4">{issue.description}</div>
                          
                          {issue.evidence && (
                            <div className="mt-2">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Relevant text from policy:</h4>
                              <blockquote className="text-sm italic border-l-4 border-gray-200 pl-4 py-2 text-gray-600">
                                {issue.evidence}
                              </blockquote>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <Shield className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <h3 className="text-lg font-medium text-gray-900">No constitutional issues identified</h3>
                    <p className="mt-1 text-gray-500">This policy does not appear to have notable constitutional concerns.</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Precedents tab */}
            {activeTab === 'precedents' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Legal Precedents</h2>
                
                {policy.precedents && policy.precedents.length > 0 ? (
                  <div className="space-y-6">
                    {policy.precedents.map((precedent, index) => (
                      <div key={index} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                          <h3 className="font-medium text-gray-900">{precedent.precedent_name}</h3>
                          <div className={`text-xs font-medium px-2 py-1 rounded-full border ${getSeverityColor(precedent.severity)}`}>
                            {getSeverityLabel(precedent.severity)} Relevance
                          </div>
                        </div>
                        
                        <div className="p-4">
                          <div className="text-sm text-gray-700 mb-4">{precedent.description}</div>
                          
                          {precedent.relevance && (
                            <div className="mt-2">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Relevance to this policy:</h4>
                              <p className="text-sm text-gray-600">{precedent.relevance}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <Globe className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <h3 className="text-lg font-medium text-gray-900">No significant legal precedents identified</h3>
                    <p className="mt-1 text-gray-500">No notable historical or legal precedents were found for this policy.</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Critical insights tab */}
            {activeTab === 'insights' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Critical Insights</h2>
                
                {policy.excerpts && policy.excerpts.length > 0 ? (
                  <div className="space-y-6">
                    {policy.excerpts.map((excerpt, index) => (
                      <div key={index} className="border rounded-lg overflow-hidden">
                        <div className="p-4">
                          <div className="flex justify-end mb-2">
                            <div className={`text-xs font-medium px-2 py-1 rounded-full border ${getSeverityColor(excerpt.importance)}`}>
                              {getSeverityLabel(excerpt.importance)} Significance
                            </div>
                          </div>
                          
                          <blockquote className="text-md italic border-l-4 border-amber-200 pl-4 py-2 text-gray-700 mb-4">
                            {excerpt.excerpt_text}
                          </blockquote>
                          
                          {excerpt.context && (
                            <div className="text-sm text-gray-600 mt-3 border-t pt-3">
                              <span className="font-medium">Context:</span> {excerpt.context}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <Bookmark className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <h3 className="text-lg font-medium text-gray-900">No critical sections available</h3>
                    <p className="mt-1 text-gray-500">No significant sections were identified for this policy.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContainer>
      </div>
        </MobileDetailView>
      </div>
    </>
  );
};

const ChevronLeft = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    {...props}
  >
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const ChevronRight = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    {...props}
  >
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

export default PolicyDetailView;